<!-- Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved. -->

<template>
  <div class="profile-input">
    <form @submit.prevent autocomplete="off">
      <input-comp
        v-if="isEmeaRegion"
        class="prefix"
        name="Title"
        :id="validationComponents.prefix"
        :ref="validationComponents.prefix"
        :label="$t('signUp.title')"
        autocomplete="honorific-prefix"
        v-model="formData.title"
        v-model:errorMessages="profileValidator.titleErrors"
        :validationRules="[required, max(12)]"
        :validateOn="validateOn"
      ></input-comp>
      <input-comp
        :class="[
          'first-name',
          isEmeaRegion ? 'name-with-title' : 'name-without-title'
        ]"
        name="First Name"
        :id="validationComponents.firstName"
        :ref="validationComponents.firstName"
        :label="$t('signUp.firstName')"
        autofocus
        autocomplete="given-name"
        v-model="formData.firstName"
        v-model:errorMessages="profileValidator.firstNameErrors"
        :validationRules="[required, max(25)]"
        :validateOn="validateOn"
      ></input-comp>
      <input-comp
        :class="[
          'last-name',
          isEmeaRegion ? 'name-with-title' : 'name-without-title'
        ]"
        name="Last Name"
        :id="validationComponents.lastName"
        :ref="validationComponents.lastName"
        :label="$t('signUp.lastName')"
        autocomplete="family-name"
        v-model="formData.lastName"
        v-model:errorMessages="profileValidator.lastNameErrors"
        :validationRules="[required, max(25)]"
        :validateOn="validateOn"
      ></input-comp>
      <input-comp
        name="Company"
        :id="validationComponents.company"
        :ref="validationComponents.company"
        :label="$t('signUp.company')"
        autocomplete="organization"
        v-model="formData.companyName"
        v-model:errorMessages="profileValidator.companyNameErrors"
        :validationRules="[required, max(60)]"
        :validateOn="validateOn"
      ></input-comp>
      <country-select-comp
        name="Country"
        :id="validationComponents.country"
        :ref="validationComponents.country"
        v-model="countryComputed"
        v-model:errorMessages="profileValidator.countryCodeErrors"
        :label="$t('signUp.country')"
        :displayFunction="formatCountryDisplay"
        :validationRules="[
          required,
          restrictedCountry,
          apacOnlyCountry,
          disabledCountry,
          differentRegion(initialRegion)
        ]"
        :validateOn="validateOn"
        width="100%"
        autocomplete="off"
      ></country-select-comp>
      <input-comp
        v-if="isSignUpForm"
        id="username"
        name="Username"
        class="not-displayed"
        :label="$t('signUp.email')"
        v-model="formData.email"
        autocomplete="username"
      ></input-comp>
      <input-comp
        v-if="isSignUpForm"
        name="Email"
        :id="validationComponents.email"
        :ref="validationComponents.email"
        class="email"
        :label="$t('signUp.email')"
        v-model.trim="formData.email"
        v-model:errorMessages="profileValidator.emailErrors"
        :validationRules="[required, max(80), emailValidator]"
        :validateOn="validateOn"
        autocomplete="email"
      ></input-comp>
      <input-comp
        v-if="isSignUpForm"
        name="Password"
        :id="validationComponents.password"
        :ref="validationComponents.password"
        type="password"
        autocomplete="new-password"
        v-model="formData.password"
        v-model:errorMessages="profileValidator.passwordErrors"
        :infoMessages="passwordStrengthMessage"
        :label="$t('signUp.password')"
        @blur="checkPasswordStrength"
        @keyup="checkPasswordStrength"
        :validationRules="[required, passwordTooShort, passwordHasProfileData(formData)]"
        :validateOn="validateOn"
      ></input-comp>
      <phone-comp
        name="Phone"
        :id="validationComponents.phone"
        :ref="validationComponents.phone"
        type="tel"
        autocomplete="tel"
        :label="$t('signUp.phone')"
        v-model="formData.phoneNumber"
        v-model:country="phoneCountryComputed"
        v-model:errorMessages="profileValidator.phoneNumberErrors"
        :countryDisplayFunction="formatCountryDisplay"
        :validationRules="[required, max(30)]"
        :validateOn="validateOn"
      ></phone-comp>
      <input-comp
        name="Postal"
        :id="validationComponents.postal"
        :ref="validationComponents.postal"
        :label="$t('signUp.postal')"
        autocomplete="postal-code"
        v-model="formData.postalCode"
        v-model:errorMessages="profileValidator.postalCodeErrors"
        :validationRules="[required, max(10)]"
        :validateOn="validateOn"
      ></input-comp>
      <slot name="submitButton"></slot>
    </form>
  </div>
</template>

<script lang="ts">
import { mapGetters, mapState } from 'vuex';
import { getterTypes } from '@/store/geolocation';
import PhoneMaskLengthMixin from '@/val/phoneMaskLengthMixin';
import { ProfileInfo } from '@/models/models';
import { FieldLengths } from '@/models/constants';
import { IState } from '@/store';
import customValidation, { IErrorMessage } from '@/val/customValidation';
import countryValidation from '@/val/countryValidation';
import passwordValidation from '@/val/passwordValidation';
import { PropType } from 'vue';
import { ValidateOn } from '@protolabs/component-library';

export default {
  setup() {
    return {
      ...customValidation,
      ...passwordValidation,
      ...countryValidation,
      FieldLengths,
    };
  },
  name: 'profile-info',
  mixins: [PhoneMaskLengthMixin],
  props: {
    model: {
        type: ProfileInfo,
        default: () => new ProfileInfo(),
    },
    context: {
        type: String,
        default: 'signUp',
    },
    validateOn: {
      type: String as PropType<ValidateOn>,
      required: true,
    }
  },
  data() {
    return {
      formData: this.model,
      checkingPasswordStrength: false,
      initialRegion: '',
      validationComponents: {
        prefix: 'prefix',
        firstName: 'firstName',
        lastName: 'lastName',
        company: 'company',
        country: 'country',
        email: 'email',
        password: 'password',
        phone: 'phone',
        postal: 'postal',
      },
      profileValidator: {
        companyNameErrors: [] as IErrorMessage[],
        countryCodeErrors: [] as IErrorMessage[],
        emailErrors: [] as IErrorMessage[],
        firstNameErrors: [] as IErrorMessage[],
        lastNameErrors: [] as IErrorMessage[],
        passwordErrors: [] as IErrorMessage[],
        phoneNumberErrors: [] as IErrorMessage[],
        postalCodeErrors: [] as IErrorMessage[],
        titleErrors: [] as IErrorMessage[],
      },
    };
  },
  computed: {
    ...mapState({
      countries: (state: any) => (state as IState).geolocation.countries,
      defaultCountryCode: (state: any) => (state as IState).geolocation.defaultCountryCode,
    }),
    ...mapGetters({
      countryByCountryCode: getterTypes.COUNTRY_BY_CODE,
    }),
    countryComputed: {
      get() {
        return this.getCountryValue(this.formData.countryCode);
      },
      set(val: {value: {code: string}}) {
        this.formData.countryCode = val.value.code;
      },
    },
		phoneCountryComputed: {
      get() {
        return this.getCountryValue(this.formData.phoneCountryCode);
			},
			set(val: {value: {code: string}}) {
				this.formData.phoneCountryCode = val.value.code;
			}
    },
    isSignUpForm() {
      return this.context === 'signUp';
    },
    isEmeaRegion() {
      var country = this.countryComputed?.value;
      return country
        ? country.region === 'EMEA'
        : false;
    },
    signInUrl() {
      return {
        name: 'Sign In',
        path: '/',
        query: this.query,
      };
    },
    query() {
      if (this.$route && this.$route.query) {
        return this.$route.query;
      }
      return {};
    },
    locale() {
      if (this.formData.locale) {
        return this.formData.locale;
      }
      if (this.$i18n && this.$i18n.locale) {
        return this.$i18n.locale;
      }
      return navigator.language;
    },
    passwordIsStrong() {
      return this.profileValidator.passwordErrors.length === 0;
    },
    passwordStrengthMessage() {
      return this.passwordIsStrong
        ? [this.$t('signUp.passwordStrong')]
        : [];
    },
  },
  watch: {
    countryComputed(country) {
      if (country && country.value.code) {
        if (!this.formData.phoneNumber) {
          this.formData.phoneCountryCode = country.value.code;
        }
      }
    },
    model() {
      this.formData = this.model;
      this.setDefaultValues();
    },
    phoneCountryComputed(val) {
      this.phoneMask = val.value.phoneMask;
    },
  },
  created() {
    this.setDefaultValues();
  },
  methods: {
    setDefaultValues() {
      let country;
      if (this.formData.countryCode) {
          country = this.countryByCountryCode(this.formData.countryCode);
      }

      if (country) {
        this.initialRegion = country.region;
      } else {
        this.formData.countryCode = this.defaultCountryCode;
      }

      this.formData.locale = this.formData.locale
        ? this.formData.locale
        : this.locale;

      this.formData.phoneCountryCode = this.formData.phoneCountryCode
        ? this.formData.phoneCountryCode.toUpperCase()
        : this.formData.countryCode;
    },
    getCountryValue(countryCode: string) {
      const country = this.countryByCountryCode(countryCode);

      return country
        ? {
          text: country.name,
          value: country,
        }
        : null;
    },
    addEmailValidationError(msg: string | null = null) {
      this.profileValidator.emailErrors = [];

      if (msg) {
        this.profileValidator.emailErrors.push({
          id: this.validationComponents.email,
          validation: '',
          errorMessageText: msg,
        });
      }
    },
    addPasswordValidationError(msg: string | null = null) {
      this.profileValidator.passwordErrors = [];

      if (msg) {
        this.profileValidator.passwordErrors.push({
          id: this.validationComponents.password,
          validation: '',
          errorMessageText: msg,
        });
      }
    },
    async checkPasswordStrength() {
      const passwordValidationResults = this.formValidator(this, [this.validationComponents.password]);

      if (passwordValidationResults.length > 0 || this.checkingPasswordStrength) {
        return;
      }

      this.checkingPasswordStrength = true;

      const result = await this.checkPasswordValidity(null, this.formData.password);

      this.addPasswordValidationError(result?.value);

      this.checkingPasswordStrength = false;
    },
    formatCountryDisplay(country: any) {
      return {
        ...country,
        ...{name: this.$t(`countries.${country.code}`)},
      };
    },
    async validate() {
      const errorItems = this.formValidator(this, this.validationComponents);

      await this.checkPasswordStrength();

      return errorItems.length === 0;
    }
  },
};
</script>

<style lang="scss" scoped>
p {
    flex-basis: 100%;
    color: $gray-med-3;
    font-size: 14px;
}
.password-rule-tips {
  @include font-body-book-regular;
  color: $secondary-gray;
}
.profile-input {
    .input, .textInputSelect {
        flex: 0 0 100%;
        margin-top: 30px;
        &.prefix {
            flex-basis: 18%;
        }
        &.name-with-title {
            flex-basis: 38%;
        }
        &.name-without-title {
            flex-basis: 48%;
        }
    }
    .not-displayed {
        display: none;
    }
    form {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
    }
}
</style>
